import React from "react";
import { useSelector } from "react-redux";
import { AgeEntry, DateRangePicker, DropDownDataSourceType, RuleType } from "@emisgroup/cohort-builder";
import { DateRange } from "@emisgroup/query-builder-library/build/dist/components/dateRangePicker/dateRangePickerTypes";
import { IRootState } from "../../../../../reducer/CombineReducer";
import {
  Input,
  Form,
  FormSection,
  FormElement,
  RadioButtons
} from "../../../../Common/UI/Index";
import { InputType } from "../../../../Common/UI/Types";
import { AgeFeature, RangeFromDataSource, RangeToDataSource, generateAgeOptions } from "./Data";
import { AgeRangeType, AgeTypes } from "./Types";
import { DateFeature } from "./MockData";
import "./Style.scss";


interface BandingAgeFormViewProps {
  /**
   * To update the date range
   */
  onUpdate: (value?: DateRange) => void;
  /**
   * To update the date range
   */
  onUpdateTo: (value?: DateRange) => void;
  /**
   * Handler for name change
   */
  onNameChange: (e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  /**
   * To update the date range
   */
  onUpdateAgeFromValue: (value: AgeRangeType) => void;
  /**
   * To update the date range
  */
  onUpdateAgeToValue: (value: AgeRangeType) => void;
  /**
   * On change handler for range types
   */
  onSelectRangeFrom: (selected: string) => void;
  /**
   * On change handler for range types
   */
  onSelectRangeTo: (selected: string) => void;
  /**
   * Selected range from option
   */
  selectRangeFromOption: string;
  /**
   * Selected range to option
   */
  selectRangeToOption: string;
  /**
   * Entered banding name in textfield
   */
  bandingName: string;
}

/**
 * Responsible for rendering age banding popup
 * @returns Age banding popup view
 */
export const BandingAgeFormView: React.FC<BandingAgeFormViewProps> = (props: BandingAgeFormViewProps) => {
  const {
    onUpdate,
    onUpdateTo,
    onNameChange,
    onUpdateAgeFromValue,
    onUpdateAgeToValue,
    onSelectRangeFrom,
    onSelectRangeTo,
    selectRangeFromOption,
    selectRangeToOption,
    bandingName
  } = props;

  const outputConfig = useSelector((state: IRootState) => state.outputConfig);
  const dateFrom = outputConfig.selectedAgeRangeFrom;
  const dateTo = outputConfig.selectedAgeRangeTo;
  const ageFromValue = outputConfig?.selectedAgeValueFrom;
  const ageToValue = outputConfig?.selectedAgeValueTo;

  const classRangeFrom = "ebt-output-config__banding__range__range-from";
  const classRangeTo = "ebt-output-config__banding__range__range-to";

  const classToDisableFromRange = selectRangeFromOption === AgeTypes.NONE ?
    `${classRangeFrom} ${classRangeFrom}--disabled` : classRangeFrom;
  const classToDisableToRange = selectRangeToOption === AgeTypes.NONE ?
    `${classRangeTo} ${classRangeTo}--disabled` : classRangeTo;

  const rangeFromOptions = generateAgeOptions("ebt-output-config-banding-range-from");
  const rangeToOptions = generateAgeOptions("ebt-output-config-banding-range-to");
  return (
    <Form>
      <FormSection>
        <FormElement
          fieldId="band_name"
          labelText="Name"
          mandatory={true}
        >
          <Input
            type={InputType.Text}
            name="band_name"
            onChange={onNameChange}
            value={bandingName}
            id="ebt-outputConfig-banding-ageBand"
          />
        </FormElement>
        <FormElement
          fieldId="band_name"
          labelText="Age Range"
          mandatory={true}
        >
          <div className="ebt-output-config__banding__range">
            <FormElement
              labelText=""
              className={classToDisableFromRange}
            >
              <div
                className="ebt-output-config__banding__range__range-from__options"
                id="ebt-outputConfig-banding-range-from"
              >
                <RadioButtons
                  data-testid="ebt-outputConfig-banding-rangeFrom"
                  defaultValue={selectRangeFromOption}
                  options={rangeFromOptions}
                  onClickEvent={onSelectRangeFrom}
                />
              </div>
              {(selectRangeFromOption === AgeTypes.DATE || selectRangeFromOption === AgeTypes.NONE) &&
                <DateRangePicker
                  value={dateFrom.value as DateRange}
                  featureDetail={DateFeature.featureDetail}
                  onChange={onUpdate}
                  options={RangeFromDataSource as unknown as DropDownDataSourceType[]}
                  hideRange={true}
                />
              }
              {selectRangeFromOption === AgeTypes.AGE &&
                <AgeEntry
                  entry={ageFromValue}
                  ruleType={RuleType.PatientDetails}
                  feature={AgeFeature}
                  features={AgeFeature}
                  onUpdate={onUpdateAgeFromValue}
                  hideRange={true}
                />
              }
            </FormElement>
            <FormElement
              labelText=""
              className={classToDisableToRange}
            >
              <div
                className="ebt-output-config__banding__range__range-to__options"
                id="ebt-outputConfig-banding-range-to"
              >
                <RadioButtons
                  data-testid="ebt-outputConfig-banding-rangeTo"
                  defaultValue={selectRangeToOption}
                  options={rangeToOptions}
                  onClickEvent={onSelectRangeTo}
                />
              </div>
              {(selectRangeToOption === AgeTypes.DATE || selectRangeToOption === AgeTypes.NONE) &&
                <DateRangePicker
                  value={dateTo.value as DateRange}
                  featureDetail={DateFeature.featureDetail}
                  onChange={onUpdateTo}
                  options={RangeToDataSource as unknown as DropDownDataSourceType[]}
                  hideRange={true}
                />
              }
              {selectRangeToOption === AgeTypes.AGE &&
                <AgeEntry
                  entry={ageToValue}
                  ruleType={RuleType.PatientDetails}
                  feature={AgeFeature}
                  features={AgeFeature}
                  onUpdate={onUpdateAgeToValue}
                  hideRange={true}
                />
              }
            </FormElement>
          </div>
        </FormElement>
      </FormSection>
    </Form>
  );
};
